import type { FC } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Select } from 'antd';

const DEFAULT_PAGE_SIZE_OPTIONS = [20, 50, 100];

type Props = {
  current: number;
  pageSize: number;
  total: number;
  pageSizeOptions?: number[];
  onChange: (page: number, pageSize: number) => void;
  className?: string;
  selectedCount?: number;
};

/** 页码列表：首尾 + 当前±1 + 省略号（对齐原型 ipc-table.js） */
function buildPageItems(current: number, totalPages: number): Array<number | 'dots'> {
  if (totalPages <= 1) return totalPages === 1 ? [1] : [];
  const pages: Array<number | 'dots'> = [1];
  if (current > 3) pages.push('dots');
  for (let i = Math.max(2, current - 1); i <= Math.min(totalPages - 1, current + 1); i += 1) {
    pages.push(i);
  }
  if (current < totalPages - 2) pages.push('dots');
  if (totalPages > 1) pages.push(totalPages);
  return pages;
}

/** 表格底部分页：左「共 N 条 + 条/页」，右描边页码（原型 data-ipc-pagination） */
export const IpcPagination: FC<Props> = ({
  current,
  pageSize,
  total,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onChange,
  className,
  selectedCount,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safeCurrent = Math.min(Math.max(1, current), totalPages);
  const pageItems = buildPageItems(safeCurrent, totalPages);

  const goPage = (page: number) => {
    if (page < 1 || page > totalPages || page === safeCurrent) return;
    onChange(page, pageSize);
  };

  const changeSize = (size: number) => {
    onChange(1, size);
  };

  return (
    <div className={['ipc-pagination', className].filter(Boolean).join(' ')}>
      <div className="ipc-pagination__info">
        <span>
          共 <strong>{total}</strong> 条
          {selectedCount != null ? <> · 已选 <strong>{selectedCount}</strong> 条</> : null}
        </span>
        <Select
          className="ipc-pagination__size"
          value={pageSize}
          options={pageSizeOptions.map(s => ({ value: s, label: `${s} 条/页` }))}
          onChange={changeSize}
          popupMatchSelectWidth={90}
        />
      </div>
      <div className="ipc-pagination__btns">
        <button
          type="button"
          className="ipc-page-btn ipc-page-btn--nav"
          disabled={safeCurrent <= 1}
          aria-label="上一页"
          onClick={() => goPage(safeCurrent - 1)}
        >
          <ChevronLeft size={16} />
        </button>
        {pageItems.map((item, idx) =>
          item === 'dots' ? (
            <span key={`dots-${idx}`} className="ipc-page-dots" aria-hidden>
              ···
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={`ipc-page-btn${item === safeCurrent ? ' ipc-page-btn--active' : ''}`}
              aria-current={item === safeCurrent ? 'page' : undefined}
              onClick={() => goPage(item)}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          className="ipc-page-btn ipc-page-btn--nav"
          disabled={safeCurrent >= totalPages}
          aria-label="下一页"
          onClick={() => goPage(safeCurrent + 1)}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};
